import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/Bits";
import { Link } from "react-router-dom";
import { Calendar, Receipt, Wallet, Bank, Coins, Lightning } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const CARD_ACCENTS = {
    "card-payables": { strip: "#c47454", tone: "#f7e4d5" },
    "card-receivables": { strip: "#2f7a75", tone: "#cfe7e4" },
    "card-expenses": { strip: "#7a3a63", tone: "#ecd6e2" },
    "card-open-advances": { strip: "#4a7a4f", tone: "#d7ecd5" },
};

const StatCard = ({ title, value, sub, testid, to }) => {
    const a = CARD_ACCENTS[testid] || {};
    return (
        <Link
            to={to || "#"}
            className="block bg-white border rounded-md p-5 hover:shadow-md transition-all relative overflow-hidden"
            style={{ borderColor: "var(--se-border)", "--se-strip": a.strip }}
            data-testid={testid}
        >
            <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: a.strip || "var(--se-brand)" }} />
            <div className="absolute -right-8 -top-8 w-24 h-24 rounded-full opacity-40" style={{ background: a.tone || "var(--se-brand-soft)" }} />
            <div className="relative">
                <div className="text-[10px] tracking-widest uppercase" style={{ color: a.strip || "var(--se-brand)" }}>{title}</div>
                <div className="font-mono text-2xl mt-2 tabular-nums" data-testid={`${testid}-value`}>₹ {inr(value)}</div>
                {sub ? <div className="text-xs text-stone-500 mt-1">{sub}</div> : null}
            </div>
        </Link>
    );
};

export default function Dashboard() {
    const { user } = useAuth();
    const isEmployee = user?.role === "employee";
    const canSeeNetWorth = user?.role === "founder" || user?.role === "accountant";
    const [payables, setPayables] = useState(null);
    const [receivables, setReceivables] = useState(null);
    const [expenses, setExpenses] = useState(null);
    const [openAdv, setOpenAdv] = useState(null);
    const [purchase, setPurchase] = useState([]);
    const [sales, setSales] = useState([]);
    const [banklines, setBanklines] = useState([]);
    const [cbp, setCbp] = useState(null); // Cash & Bank position — per-account balances
    // Net worth (founder + accountant)
    const [netWorth, setNetWorth] = useState(null);
    const [nwHistory, setNwHistory] = useState(null);
    // Employee-specific
    const [profile, setProfile] = useState(null);
    const [balance, setBalance] = useState(null);
    const [latestSlip, setLatestSlip] = useState(null);

    useEffect(() => {
        if (isEmployee) {
            (async () => {
                const [p, b, s] = await Promise.all([
                    api.get("/me/profile").then((x) => x.data).catch(() => null),
                    api.get("/leaves/balance").then((x) => x.data).catch(() => null),
                    api.get("/me/payslips").then((x) => x.data).catch(() => []),
                ]);
                setProfile(p);
                setBalance(b);
                setLatestSlip((s || [])[0] || null);
            })();
            return;
        }
        (async () => {
            const [p, r, e, a, pi, si, bl, nw, nwh, cbpr] = await Promise.all([
                api.get("/reports/payables").then((x) => x.data).catch(() => null),
                api.get("/reports/receivables").then((x) => x.data).catch(() => null),
                api.get("/reports/expenses").then((x) => x.data).catch(() => null),
                api.get("/reports/advances-open").then((x) => x.data).catch(() => null),
                api.get("/purchase-invoices?limit=5").then((x) => x.data).catch(() => []),
                api.get("/sales-invoices?limit=5").then((x) => x.data).catch(() => []),
                api.get("/bank-lines?status=unmatched&limit=5").then((x) => (x.data.items || x.data || [])).catch(() => []),
                canSeeNetWorth ? api.get("/reports/networth").then((x) => x.data).catch(() => null) : Promise.resolve(null),
                canSeeNetWorth ? api.get("/reports/networth/history?months=12").then((x) => x.data).catch(() => null) : Promise.resolve(null),
                api.get("/reports/cash-bank-position").then((x) => x.data).catch(() => null),
            ]);
            setPayables(p);
            setReceivables(r);
            setExpenses(e);
            setOpenAdv(a);
            setPurchase(pi);
            setSales(si);
            setBanklines(bl);
            setNetWorth(nw);
            setNwHistory(nwh);
            setCbp(cbpr);
        })();
    }, [isEmployee, canSeeNetWorth]);

    const Card = StatCard;

    if (isEmployee) {
        return (
            <>
                <PageHeader title={`Hi, ${profile?.name || user?.name || "there"}`} sub="Your leave balance, latest payslip, and shortcuts." />
                <div className="p-8 space-y-6" data-testid="employee-dashboard">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Link to="/leaves" className="block bg-white border rounded-md p-5 hover:shadow-md transition-all" style={{ borderColor: "var(--se-border)" }} data-testid="emp-card-cl">
                            <div className="flex items-center gap-2 text-xs tracking-widest uppercase" style={{ color: "#0f766e" }}>
                                <Calendar size={14} /> Casual leaves
                            </div>
                            <div className="se-num text-3xl mt-2" style={{ color: "#0f766e" }}>
                                {balance?.casual?.available ?? "—"} <span className="text-sm text-stone-500">/ {balance?.casual?.quota ?? 12}</span>
                            </div>
                            <div className="text-xs text-stone-500 mt-1">Used {balance?.casual?.used ?? 0}{balance?.casual?.pending ? ` · Pending ${balance.casual.pending}` : ""}</div>
                        </Link>
                        <Link to="/leaves" className="block bg-white border rounded-md p-5 hover:shadow-md transition-all" style={{ borderColor: "var(--se-border)" }} data-testid="emp-card-ml">
                            <div className="flex items-center gap-2 text-xs tracking-widest uppercase" style={{ color: "#0369a1" }}>
                                <Calendar size={14} /> Medical leaves
                            </div>
                            <div className="se-num text-3xl mt-2" style={{ color: "#0369a1" }}>
                                {balance?.medical?.available ?? "—"} <span className="text-sm text-stone-500">/ {balance?.medical?.quota ?? 15}</span>
                            </div>
                            <div className="text-xs text-stone-500 mt-1">Used {balance?.medical?.used ?? 0}{balance?.medical?.pending ? ` · Pending ${balance.medical.pending}` : ""}</div>
                        </Link>
                        <Link to="/my-payslips" className="block bg-white border rounded-md p-5 hover:shadow-md transition-all" style={{ borderColor: "var(--se-border)" }} data-testid="emp-card-slip">
                            <div className="flex items-center gap-2 text-xs tracking-widest uppercase" style={{ color: "#b45309" }}>
                                <Receipt size={14} /> Latest payslip
                            </div>
                            {latestSlip ? (
                                <>
                                    <div className="se-num text-3xl mt-2" style={{ color: "#b45309" }}>₹ {inr(latestSlip.net_payable)}</div>
                                    <div className="text-xs text-stone-500 mt-1">{latestSlip.month} · {latestSlip.status}</div>
                                </>
                            ) : (
                                <div className="text-sm text-stone-500 mt-3">Not posted yet</div>
                            )}
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Link to="/leaves" className="border rounded-md p-4 hover:bg-stone-50 text-sm" style={{ borderColor: "var(--se-border)" }}>
                            <Calendar size={14} className="inline mr-2" /> Apply for leave
                        </Link>
                        <Link to="/my-payslips" className="border rounded-md p-4 hover:bg-stone-50 text-sm" style={{ borderColor: "var(--se-border)" }}>
                            <Receipt size={14} className="inline mr-2" /> View past payslips
                        </Link>
                        <Link to="/petty-cash" className="border rounded-md p-4 hover:bg-stone-50 text-sm" style={{ borderColor: "var(--se-border)" }}>
                            <Wallet size={14} className="inline mr-2" /> Raise petty-cash request
                        </Link>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <PageHeader title={`Good day, ${user?.name || "Friend"}`} sub="A quiet, running view of the money — for the whole business." />
            <div className="p-8 space-y-8">
                {canSeeNetWorth && netWorth ? <NetWorthHero snap={netWorth} history={nwHistory} /> : null}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card testid="card-payables" title="Payables" value={payables?.total} sub={`${payables?.items?.length || 0} parties`} to="/reports/payables" />
                    <Card testid="card-receivables" title="Receivables" value={receivables?.total} sub={`${receivables?.items?.length || 0} parties`} to="/reports/receivables" />
                    <Card testid="card-expenses" title="Expenses (all-time)" value={expenses?.total} sub={`Direct ₹${inr(expenses?.total_direct)} · Indirect ₹${inr(expenses?.total_indirect)}`} to="/reports/expenses" />
                    <Card testid="card-open-advances" title="Open customer advances" value={openAdv?.total} sub={`${openAdv?.items?.length || 0} open`} to="/advances" />
                </div>

                {cbp?.accounts?.length ? <CashBankStrip cbp={cbp} /> : null}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                        <div className="px-5 py-3 flex items-center justify-between border-b" style={{ borderColor: "var(--se-border)" }}>
                            <div className="font-serif text-lg">Recent purchase invoices</div>
                            <Link to="/purchase" className="text-xs uppercase tracking-widest" style={{ color: "var(--se-brand)" }} data-testid="link-all-purchase">All</Link>
                        </div>
                        <div className="p-3">
                            {purchase.length === 0 ? <div className="text-sm text-stone-500 p-4">No invoices yet</div> :
                                purchase.map((p) => (
                                    <Link to={`/purchase/${p.id}`} key={p.id} className="flex items-center justify-between px-2 py-2 rounded hover:bg-stone-50">
                                        <div>
                                            <div className="text-sm">{p.vendor_name || "—"}</div>
                                            <div className="text-xs text-stone-500">{p.invoice_no || p.id.slice(0, 6)}</div>
                                        </div>
                                        <div className="font-mono text-sm tabular-nums">₹ {inr(p.total)}</div>
                                    </Link>
                                ))
                            }
                        </div>
                    </div>
                    <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                        <div className="px-5 py-3 flex items-center justify-between border-b" style={{ borderColor: "var(--se-border)" }}>
                            <div className="font-serif text-lg">Recent sales invoices</div>
                            <Link to="/sales" className="text-xs uppercase tracking-widest" style={{ color: "var(--se-brand)" }} data-testid="link-all-sales">All</Link>
                        </div>
                        <div className="p-3">
                            {sales.length === 0 ? <div className="text-sm text-stone-500 p-4">No invoices yet</div> :
                                sales.map((s) => (
                                    <Link to={`/sales/${s.id}`} key={s.id} className="flex items-center justify-between px-2 py-2 rounded hover:bg-stone-50">
                                        <div>
                                            <div className="text-sm">{s.customer_name || "—"}</div>
                                            <div className="text-xs text-stone-500">{s.invoice_no || s.id.slice(0, 6)}</div>
                                        </div>
                                        <div className="font-mono text-sm tabular-nums">₹ {inr(s.total)}</div>
                                    </Link>
                                ))
                            }
                        </div>
                    </div>
                    <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                        <div className="px-5 py-3 flex items-center justify-between border-b" style={{ borderColor: "var(--se-border)" }}>
                            <div className="font-serif text-lg">Bank lines waiting for you</div>
                            <Link to="/bank" className="text-xs uppercase tracking-widest" style={{ color: "var(--se-brand)" }} data-testid="link-all-bank">All</Link>
                        </div>
                        <div className="p-3">
                            {banklines.length === 0 ? <div className="text-sm text-stone-500 p-4">No unmatched bank lines</div> :
                                banklines.map((b) => (
                                    <div key={b.id} className="flex items-center justify-between px-2 py-2 rounded hover:bg-stone-50">
                                        <div>
                                            <div className="text-xs text-stone-500">{b.bank} · {b.date}</div>
                                            <div className="text-sm truncate max-w-[220px]">{b.narration}</div>
                                        </div>
                                        <div className="font-mono text-sm tabular-nums">
                                            {b.credit ? <span style={{ color: "#1e4620" }}>+ ₹{inr(b.credit)}</span> : null}
                                            {b.debit ? <span style={{ color: "#991b1b" }}>- ₹{inr(b.debit)}</span> : null}
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function NetWorthHero({ snap, history }) {
    const nw = snap?.net_worth ?? 0;
    const positive = nw >= 0;
    const points = history?.points || [];
    // Delta vs earliest snapshot
    const first = points[0]?.net_worth;
    const delta = (first != null) ? (nw - first) : null;
    const deltaPct = (first && first !== 0) ? ((delta / Math.abs(first)) * 100) : null;
    return (
        <Link to="/reports/networth" className="block rounded-md border p-6 hover:shadow-md transition-all"
              style={{
                  borderColor: "var(--se-border)",
                  background: positive
                      ? "linear-gradient(135deg,#ecfdf5 0%,#f7fee7 100%)"
                      : "linear-gradient(135deg,#fef2f2 0%,#fffbeb 100%)",
              }}
              data-testid="dash-networth">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                    <div className="text-xs tracking-widest uppercase" style={{ color: positive ? "#065f46" : "#991b1b" }}>
                        Net worth · as of {snap.as_of}
                    </div>
                    <div className="se-num text-4xl mt-2" style={{ color: positive ? "#065f46" : "#991b1b" }}>
                        {positive ? "" : "− "}₹ {inr(Math.abs(nw))}
                    </div>
                    <div className="text-xs text-stone-700 mt-2">
                        {positive
                            ? "You'd keep this much if you closed the business today."
                            : "You'd need this much to shut everything down cleanly."}
                    </div>
                    {delta != null ? (
                        <div className="text-xs mt-3">
                            <span className="text-stone-500">Change over {points.length} months: </span>
                            <span style={{ color: delta >= 0 ? "#065f46" : "#991b1b" }}>
                                {delta >= 0 ? "+ " : "− "}₹ {inr(Math.abs(delta))}
                                {deltaPct != null ? ` (${deltaPct >= 0 ? "+" : ""}${deltaPct.toFixed(1)}%)` : ""}
                            </span>
                        </div>
                    ) : null}
                </div>
                <div style={{ height: 130 }}>
                    {points.length > 0 ? (
                        <ResponsiveContainer>
                            <LineChart data={points} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                                <XAxis dataKey="label" fontSize={9} interval="preserveStartEnd" />
                                <YAxis fontSize={9} tickFormatter={(v) => (Math.abs(v) >= 100000 ? `${(v / 100000).toFixed(1)}L` : `${(v / 1000).toFixed(0)}k`)} width={40} />
                                <Tooltip formatter={(v) => `₹ ${inr(v)}`} />
                                <Line type="monotone" dataKey="net_worth" stroke={positive ? "#0f766e" : "#dc2626"} strokeWidth={2.5} dot={{ r: 2 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : null}
                </div>
            </div>
        </Link>
    );
}



// Per-account cash/bank strip on the founder dashboard.
// One card per active bank + cash ledger (SBI, ICICI, CASH IN HAND, FIAT BANK, etc.).
// Colour is tied to the ledger role so the eye can pick them out at a glance.
function accountTheme(acc) {
    const n = (acc.name || "").toUpperCase();
    if (n === "CASH IN HAND") return { strip: "#f59e0b", tone: "#fef3c7", icon: <Coins size={16} weight="duotone" style={{ color: "#f59e0b" }} /> };
    if (n === "FIAT BANK")    return { strip: "#e11d48", tone: "#ffe4e6", icon: <Lightning size={16} weight="duotone" style={{ color: "#e11d48" }} /> };
    if (n.includes("SBI"))    return { strip: "#0f766e", tone: "#ccfbf1", icon: <Bank size={16} weight="duotone" style={{ color: "#0f766e" }} /> };
    if (n.includes("ICICI"))  return { strip: "#c2410c", tone: "#fed7aa", icon: <Bank size={16} weight="duotone" style={{ color: "#c2410c" }} /> };
    // Any user-added bank
    return { strip: "#1d4ed8", tone: "#dbeafe", icon: <Bank size={16} weight="duotone" style={{ color: "#1d4ed8" }} /> };
}

function AccountCard({ acc }) {
    const t = accountTheme(acc);
    const negative = acc.balance < 0;
    return (
        <Link
            to={`/ledgers/${acc.id}`}
            className="block bg-white border rounded-md p-4 hover:shadow-md transition-all relative overflow-hidden"
            style={{ borderColor: "var(--se-border)" }}
            data-testid={`cbp-card-${acc.id}`}
        >
            <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: t.strip }} />
            <span className="absolute -right-6 -top-6 w-16 h-16 rounded-full opacity-40" style={{ background: t.tone }} />
            <div className="relative">
                <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase" style={{ color: t.strip }}>
                    {t.icon}
                    <span className="truncate">{acc.name}</span>
                </div>
                <div
                    className="font-mono text-xl sm:text-2xl mt-2 tabular-nums truncate"
                    style={{ color: negative ? "#991b1b" : "#111" }}
                    data-testid={`cbp-card-${acc.id}-value`}
                    title={`₹ ${inr(acc.balance)}`}
                >
                    {negative ? "− " : ""}₹ {inr(Math.abs(acc.balance))}
                </div>
                <div className="text-[10px] text-stone-500 mt-1 uppercase tracking-widest">
                    {acc.type === "cash" ? "Hard cash" : "Bank account"}
                </div>
            </div>
        </Link>
    );
}

function CashBankStrip({ cbp }) {
    const accounts = cbp?.accounts || [];
    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="dash-cash-bank">
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                <div>
                    <div className="font-serif text-lg">Cash & bank position</div>
                    <div className="text-xs text-stone-500">
                        Combined ₹ {inr(cbp.grand_total)} · Banks ₹ {inr(cbp.total_bank)} · Hard cash ₹ {inr(cbp.total_cash)}
                    </div>
                </div>
                <Link to="/cash-bank" className="text-xs uppercase tracking-widest" style={{ color: "var(--se-brand)" }} data-testid="link-all-cbp">
                    Details
                </Link>
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {accounts.map((acc) => <AccountCard key={acc.id} acc={acc} />)}
            </div>
        </div>
    );
}
